import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { decode } from "jsonwebtoken";
import { get } from "lodash";
import React, { useEffect, useReducer } from "react";
import { Input } from "../../../styled/Inputs";
import { sendSignupEventToAnalytics } from "../../../utils/analytics";
import { useQueryString } from "../../../utils/url";
import Checkbox from "../../common/Checkbox";
import LoadingIndicator from "../../common/LoadingIndicator";
import MlsBundle from "../../MlsBundle";
import IconCloudAgentSuite from "../icons/IconCloudAgentSuite";
import {
  Activate,
  Cell,
  Container,
  Error,
  FormGroup,
  Grid,
  Header,
  Label,
  LabelError,
  Loading,
  LoginOption,
  LoginOptions,
  Logo,
  MLS,
  Text,
  Title,
  TOSText,
  Well
} from "./styled/sso";

function reducer(state, { type, payload }) {
  switch (type) {
    case "MLS_RESOLVED":
      return {
        ...state,
        isLoadingMls: false,
        mlsName: payload.mlsName,
        mlsId: payload.mlsId
      };
    case "UPDATE_DETAILS":
      return { ...state, ...payload };
    case "TOGGLE_TOS":
      return { ...state, tos: payload };
    case "ACTIVATE_SSO_REQUESTED":
      return {
        ...state,
        hasError: false,
        hasEmailError: false,
        isActivating: true
      };
    case "ACTIVATE_SSO_REJECTED":
      return { ...state, isActivating: false, hasEmailError: true };
    case "ERROR":
      return { ...state, isActivating: false, hasError: true };
    default:
      return state;
  }
}

function SSO({ code }) {
  const params = useQueryString();
  const token = params.jwt || localStorage.getItem("token");
  const jwtPayload = decode(token);

  useEffect(() => {
    if (jwtPayload) {
      localStorage.setItem("token", token);
    } else {
      window.location.replace(window.location.origin + "/pricing");
    }

    window.history.pushState(
      {},
      document.title,
      window.location.origin + window.location.pathname
    );
  }, []);

  const [state, dispatch] = useReducer(reducer, {
    name: get(jwtPayload, "sso.user.name", ""),
    email: get(jwtPayload, "sso.user.email", ""),
    tos: false,
    mlsId: "",
    mlsName: "",
    isActivating: false,
    isLoadingMls: true,
    hasEmailError: false,
    hasError: false
  });

  const [ssoFastrackSignup] = useMutation(SSO_FASTRACK_MUTATION, {
    onCompleted: (data) => {
      sendSignupEventToAnalytics({
        sku: "site_license",
        revenue: 0,
        promo: ""
      });
      const { jwt } = data.ssoFastrackSignup || {};
      window.localStorage.setItem("token", jwt);
      window.location.replace(`${window.location.origin}/app/launchpad`);
    },
    onError: (error) => {
      error.graphQLErrors
        ? dispatch({ type: "ACTIVATE_SSO_REJECTED" })
        : dispatch({ type: "ERROR" });
    }
  });

  const handleChange = (e) =>
    dispatch({
      type: "UPDATE_DETAILS",
      payload: { [e.target.name]: e.target.value }
    });

  const handleMlsBundleLoad = ({ mls }) =>
    dispatch({
      type: "MLS_RESOLVED",
      payload: { mlsName: mls.name, mlsId: mls.id }
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch({ type: "ACTIVATE_SSO_REQUESTED" });

    const variables = {
      user: { name: state.name, email: state.email, mls_id: state.mlsId },
      jwt: token
    };
    ssoFastrackSignup({ variables });
  };

  // useEffect(() => {
  //   if (error) {
  //     if (error.graphQLErrors) {
  //       dispatch({ type: "ACTIVATE_SSO_REJECTED" });
  //       return;
  //     }

  //     dispatch({ type: "ERROR" });
  //   }
  // }, [error]);

  // useEffect(() => {
  //   if (data) {
  //     const { jwt } = data.ssoFastrackSignup || {};

  //     window.localStorage.setItem("token", jwt);
  //     window.location.replace(`${window.location.origin}/app/launchpad`);
  //   }
  // }, [data]);

  return (
    <Container>
      <Header>
        <Logo>
          <IconCloudAgentSuite />
        </Logo>
        <MLS>
          {state.isLoadingMls && (
            <Loading>
              <LoadingIndicator />
              Loading...
            </Loading>
          )}
          {!state.isLoadingMls && state.mlsName}
        </MLS>
      </Header>
      <Well>
        <Title>Activate your account</Title>
        <Text>
          In order to access your products, you need to activate your free
          account.
        </Text>
        <Grid>
          <Cell>
            <MlsBundle
              code={code}
              bundleKey="site_license"
              onLoad={handleMlsBundleLoad}
            />
          </Cell>
          <Cell>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  name="name"
                  id="name"
                  autoFocus
                  value={state.name}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">
                  Email{" "}
                  {state.hasEmailError && <LabelError>Email taken</LabelError>}
                </Label>
                <Input
                  name="email"
                  id="email"
                  type="email"
                  value={state.email}
                  onChange={handleChange}
                />
                {state.hasEmailError && (
                  <LoginOptions>
                    <LoginOption
                      href={`/app/login${
                        state.email.length ? `?email=${state.email}` : ""
                      }`}>
                      Sign in
                    </LoginOption>{" "}
                    or{" "}
                    <LoginOption href="/users/password/new">
                      Reset your password
                    </LoginOption>
                  </LoginOptions>
                )}
              </FormGroup>
              <FormGroup>
                <Checkbox
                  name="tos"
                  value={state.tos}
                  onChange={() =>
                    dispatch({ type: "TOGGLE_TOS", payload: !state.tos })
                  }>
                  <TOSText>
                    I have read and agree with the{" "}
                    <a href="/tos" target="_blank" tabIndex={-1}>
                      Terms of Service
                    </a>
                    ,<br />{" "}
                    <a
                      href="http://wrstudios.com/privacy_policy"
                      target="_blank"
                      tabIndex={-1}>
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href="http://wrstudios.com/dmca_policy"
                      target="_blank"
                      tabIndex={-1}>
                      DMCA Policy
                    </a>
                  </TOSText>
                </Checkbox>
              </FormGroup>
              <FormGroup>
                <Activate
                  disabled={
                    state.isActivating ||
                    !state.name ||
                    !state.email ||
                    !state.tos
                  }>
                  {state.isActivating ? "Activating..." : "Activate"}
                </Activate>
              </FormGroup>
            </form>
          </Cell>
        </Grid>
        {state.hasError && (
          <Error>
            An error occured while activating your account. Please try again.
          </Error>
        )}
      </Well>
    </Container>
  );
}

export const SSO_FASTRACK_MUTATION = gql`
  mutation SSOFastrackMutation(
    $user: SsoUserRegistrationInput!
    $jwt: String!
  ) {
    ssoFastrackSignup(user: $user, sso_jwt: $jwt) {
      jwt
    }
  }
`;

export default SSO;
